import { createSelector } from 'reselect';
import { getCompanyDietCaloriesDetails } from './orderFormDietDetailsSelector';
import {
  currentDiscountDetails,
  getDietExchangeSinglePrice,
} from 'utilsRoot/orderFormPrices';
import { getOrderSettings } from './formSettingsSelector';
import { getPromoCode } from './basketSelector';
import { getISODateFromMillis } from '@adapters/dates/dates.adapter';

export const getBasketReducer = state => state.basketReducer;

export const getOrderForm = state => state.orderForm;
export const getDays = state => getOrderForm(state).days;

export const getSelectedOption = state => getOrderForm(state).selectedOption;
export const getSelectedCalories = state =>
  getOrderForm(state).selectedCalories;
export const getSelectedCaloriesId = state =>
  getSelectedCalories(state).dietCaloriesId;
export const getSelectedDays = state => getOrderForm(state).selectedDays;
export const getSelectedDaysISO = state =>
  getSelectedDays(state).map(day => getISODateFromMillis(day));

export const getSelectedMeals = state => getOrderForm(state).selectedMeals;
export const getSelectedProgram = state => getOrderForm(state).selectedProgram;
export const getMinMaxModalStatus = state =>
  getOrderForm(state).minMaxModalStatus;

export const getSelectedDiet = state => getOrderForm(state).selectedDiet;
export const getSelectedDietId = state => getSelectedDiet(state).dietId;
export const getSelectedDietDiscounts = state =>
  getSelectedDiet(state).discounts;

export const getMealExchange = state => getOrderForm(state).mealExchange;
export const getMealExchangeDays = state => getMealExchange(state).days;
export const getMealExchangeActiveDay = state =>
  getMealExchange(state).activeDay;
export const getSelectedMeal = state =>
  getMealExchange(state).selectedMealModal.selectedMeal;
export const getSelectedMealModalStatus = state =>
  getMealExchange(state).selectedMealModal.modalStatus;
export const getCaloriesModal = state => getMealExchange(state).caloriesModal;
export const getMealExchangeBasicPricePerDay = state =>
  getMealExchange(state).basicPricePerDay;
export const getMealExchangeEditModalClickedArea = state =>
  getMealExchange(state).mealExchangeEditModalClickedArea;
export const getMealExchangeEditModalStatus = state =>
  getMealExchange(state).mealExchangeEditModalStatus;

export const getCurrentDiscountDetails = createSelector(
  [getDays, getSelectedDietDiscounts],
  (daysAmount, discounts) => currentDiscountDetails(discounts, daysAmount)
);

export const getDietClosestDiscountDetails = createSelector(
  [getDays, getSelectedDietDiscounts],
  (daysAmount, discounts) => {
    if (!(daysAmount && discounts)) {
      return null;
    }
    if (daysAmount === 0) {
      return null;
    }
    const closestDiscount = discounts.reduce(
      (acc, curr) => {
        const accMinusDays = acc.minimumDays - daysAmount;
        const currMinusDay = curr && curr.minimumDays - daysAmount;
        if (accMinusDays <= 0) {
          return curr;
        }
        if (currMinusDay <= 0) {
          return acc;
        }
        if (accMinusDays < currMinusDay) {
          return acc;
        }
        return curr;
      },
      { minimumDays: 0, discount: 0, discountType: 'PERCENTAGE' }
    );

    return {
      ...closestDiscount,
      barPercentage: (daysAmount * 100) / closestDiscount.minimumDays,
      daysTillDiscount:
        closestDiscount && closestDiscount.minimumDays - daysAmount,
    };
  }
);

export const getBiggestDiscount = createSelector(
  [getSelectedDietDiscounts],
  discounts => {
    if (!discounts) {
      return null;
    }
    return (
      discounts &&
      discounts.reduce((acc, curr, index) => {
        if (index === 0) {
          return curr;
        }

        if (acc?.minimumDays < (curr && curr?.minimumDays)) {
          acc = curr;
        }
        return acc;
      }, {})
    );
  }
);

export const getDiscountAmount = createSelector(
  [getCurrentDiscountDetails],
  discountDetails => {
    if (!discountDetails || !discountDetails.discount) {
      return null;
    }
    if (discountDetails && discountDetails.discountType === 'PERCENTAGE') {
      return `${discountDetails.discount}% rabatu za długość zamówienia`;
    }
    return (
      discountDetails &&
      `${discountDetails.discount}PLN/dzień rabatu za długość zamówienia`
    );
  }
);

export const getCalculatedMealsCaloriesPricePerDay = createSelector(
  [
    getMealExchangeDays,
    getMealExchangeActiveDay,
    getMealExchangeBasicPricePerDay,
    getCurrentDiscountDetails,
    getPromoCode,
  ],
  (days, activeDay, basicPricePerDay, currentDiscount, code) => {
    const { meals, minimum, sideOrders } = basicPricePerDay;
    const selectedSideOrdersLength = days[activeDay]?.sideOrders
      ?.filter(item => item.amount)
      .reduce((acc, cur) => {
        acc = acc + cur.amount;
        return acc;
      }, 0);

    // Meals prices
    const mealsPriceAdjustment = days[activeDay]?.dayPriceAdjustment?.meals;

    const adjustedMealsPrice = meals + mealsPriceAdjustment;
    const mealsPricePerDay =
      adjustedMealsPrice - minimum <= 0 ? minimum : adjustedMealsPrice;

    const calculatedMealsPricePerDay =
      getDietExchangeSinglePrice(mealsPricePerDay, currentDiscount, code) || 0;

    // Side orders prices
    const sideOrdersPriceAdjustment =
      days[activeDay]?.dayPriceAdjustment?.sideOrders || 0;
    const sideOrdersDayPrice = sideOrders + sideOrdersPriceAdjustment;

    // Final day price
    const calculatedDayPrice = calculatedMealsPricePerDay + sideOrdersDayPrice;

    return Object.values(days[activeDay]?.mealsList || {}).reduce(
      (acc, cur) => {
        if (cur.selected) {
          const filteredList = cur.list.find(item => item.selected);

          if (filteredList) {
            const { ingredientCalories } = filteredList;

            acc.ingredientCalories =
              acc.ingredientCalories + (ingredientCalories || 0);
            ++acc.selectedMealsLength;
          }
        }

        return acc;
      },
      {
        calculatedDayPrice,
        ingredientCalories: 0,
        selectedMealsLength: 0,
        selectedSideOrdersLength,
      }
    );
  }
);

export const getMinMaxSelectedMeals = createSelector(
  [getOrderSettings, getSelectedCaloriesId, getCompanyDietCaloriesDetails],
  (orderSettings, selectedCaloriesId, companyDietCaloriesDetails) => {
    const { maximumSelectedMeals, minimumSelectedMeals } = orderSettings || {};

    const min = minimumSelectedMeals ? minimumSelectedMeals : 1;

    const find = companyDietCaloriesDetails.find(
      item => item.dietCaloriesId === selectedCaloriesId
    );

    if (
      find &&
      (find?.menuConfigurationMaxMealsNumber ||
        find?.menuConfigurationMaxMealsNumber === 0)
    ) {
      return { min, max: find.menuConfigurationMaxMealsNumber };
    }

    return { min, max: maximumSelectedMeals ? maximumSelectedMeals : 0 };
  }
);

export const getIsMenuConfigurationStared = createSelector(
  [getMealExchangeDays],
  days =>
    Object.values(days || {})?.some(
      item => Object.values(item?.mealsList || {})?.length
    )
);
